import { apiGet } from '../../../shared/services/apiClient';

interface TotalRegisteredEmpResponse {
  success: boolean;
  totalRegisteredEmp: number;
  totalContractEmpCount: number;
  totalEmployee: number;
  message: string;
}

interface TotalTeacherAttendanceResponse {
  success: boolean;
  totalPresentToday: number;
  totalPresentRegToday: number;
  totalContractualPresentToday: number;
}

interface TotalTeacherAbsenceResponse {
  success: boolean;
  totalAbsentToday: number;
  totalAbsentRegToday: number;
  totalContractualAbsentToday: number;
}

interface TotalPreviousDayAttendanceResponse {
  success: boolean;
  totalPreviousDayAttendance: number;
  totalPreviousDayAttendanceReg: number;
  totalPreviousDayContractualAttendance: number;
}

interface AttendanceByDateData {
  totalEnrollmentReg: number;
  totalPresentReg: number;
  totalAbsentReg: number;
  totalEnrollmentCon: number;
  totalPresentCon: number;
  totalAbsentCon: number;
  totalNoResponse: number;
  totalNoResponseCon: number;
}

interface AttendanceByKVSGroupingResponse {
  success: boolean;
  data: {
    [date: string]: AttendanceByDateData[];
  };
}

interface AttendanceByDateData {
  totalEnrollmentReg: number;
  totalPresentReg: number;
  totalAbsentReg: number;
  totalEnrollmentCon: number;
  totalPresentCon: number;
  totalAbsentCon: number;
  totalNoResponse: number;
  totalNoResponseCon: number;
}

interface AttendanceByKVSGroupingResponse {
  success: boolean;
  data: {
    [date: string]: AttendanceByDateData[];
  };
}

export const attendanceApi = {
  // Get total registered employees
  getTotalEmployees: async (): Promise<TotalRegisteredEmpResponse | null> => {
    try {
      const data = await apiGet('/pis/totalregisteredemp');
      if (data && data.success) {
        return {
          success: data.success,
          totalRegisteredEmp: data.totalRegisteredEmp,
          totalContractEmpCount: data.totalContractEmpCount,
          totalEmployee: data.totalEmployee,
          message: data.message
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching total registered employees:', error);
      return null;
    }
  },

  // Get total teacher attendance
  getTotalTeacherAttendance: async (): Promise<TotalTeacherAttendanceResponse | null> => {
    try {
      const data = await apiGet('/pis/totalteacherattendance');
      if (data && data.success) {
        return {
          success: data.success,
          totalPresentToday: data.totalPresentToday,
          totalPresentRegToday: data.totalPresentRegToday,
          totalContractualPresentToday: data.totalContractualPresentToday,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching total teacher attendance:', error);
      return null;
    }
  },

   // Get staff present today
   getStaffPresentToday: async (): Promise<TotalTeacherAttendanceResponse | null> => {
    try {
      const data = await apiGet('/pis/presenttoday');
      if (data && data.success) {
        return {
          success: data.success,
          totalPresentToday: data.totalPresentToday,
          totalPresentRegToday: data.totalPresentRegToday,
          totalContractualPresentToday: data.totalContractualPresentToday,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching staff present today:', error);
      return null;
    }
  },
  
  // Get staff absent today
  getStaffAbsentToday: async (): Promise<TotalTeacherAbsenceResponse | null> => {
    try {
      const data = await apiGet('/pis/absenttoday');
      if (data && data.success) {
        return {
          success: data.success,
          totalAbsentToday: data.absentToday,
          totalAbsentRegToday: data.absentRegToday,
          totalContractualAbsentToday: data.absentConToday,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching staff absent today:', error);
      return null;
    }
  },

  // Get total previous day attendance
  getTotalPreviousDayAttendance: async (): Promise<TotalPreviousDayAttendanceResponse | null> => {
    try {
      const data = await apiGet('/pis/totalpreviousdayattendance');
      if (data && data.success) {
        return {
          success: data.success,
          totalPreviousDayAttendance: data.totalPreviousDayAttendance,
          totalPreviousDayAttendanceReg: data.totalRegPreviousDayAttendance,
          totalPreviousDayContractualAttendance: data.totalContractualPreviousDayAttendance,
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching total previous day attendance:', error);
      return null;
    }
  },  

  // Get Staff Attendance Register (Employee Attendance List)
  getStaffAttendanceRegister: async (page: number = 1, year?: number, month?: number, date?: number, empType?: 'regular' | 'contractual'): Promise<any> => {
    try {
      let url = `/pis/employeeattendance?page=${page}`;
      if (year !== undefined) url += `&year=${year}`;
      if (month !== undefined) url += `&month=${String(month).padStart(2, '0')}`;
      if (date !== undefined) url += `&date=${String(date).padStart(2, '0')}`;
      if (empType === 'contractual') url += `&emptype=contractual`;
      else if (empType === 'regular') url += `&emptype=regular`;

      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching staff attendance register:', error);
      return null;
    }
  },

  // Get Staff Daily Attendance (todays-all-emp-attendance)
  getStaffDailyAttendance: async (page: number = 1, year?: number, month?: number, date?: number, empType?: 'regular' | 'contractual'): Promise<any> => {
    try {
      let url = `/pis/todays-all-emp-attendance?page=${page}`;
      if (year !== undefined) url += `&year=${year}`;
      if (month !== undefined) url += `&month=${String(month).padStart(2, '0')}`;
      if (date !== undefined) url += `&date=${String(date).padStart(2, '0')}`;
      if (empType === 'contractual') url += `&emptype=contractual`;
      else if (empType === 'regular') url += `&emptype=regular`;

      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching staff daily attendance:', error);
      return null;
    }
  },

  // Get Employee Profile (for contractual, pass emptype=contractual)
  getEmployeeProfile: async (empId: number | string, emptype?: string): Promise<any> => {
    try {
      const url = emptype ? `/pis/profile/${empId}?emptype=${emptype}` : `/pis/profile/${empId}`;
      const data = await apiGet(url);
      if (data && data.success) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching employee profile:', error);
      return null;
    }
  },

  // Get monthly permanent employee attendance (new detailed endpoint)
  getMonthlyEmployeeAttendance: async (page: number = 1, year: number, month: number): Promise<any> => {
    try {
      const formattedMonth = String(month).padStart(2, '0');
      const data = await apiGet(`/pis/monthlyemployeeattendance?page=${page}&year=${year}&month=${formattedMonth}`);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching monthly employee attendance:', error);
      return null;
    }
  },

  // Get Contractual Employee Attendance
  getContractualEmployeeAttendance: async (page: number = 1, year: number = 25, month?: number, date?: number): Promise<any> => {
    try {
      let url = `/pis/employeeattendance?page=${page}&year=${year}&emptype=contractual`;
      if (month !== undefined) {
        url += `&month=${String(month).padStart(2, '0')}`;
      }
      if (date !== undefined) {
        url += `&date=${String(date).padStart(2, '0')}`;
      }
      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching contractual employee attendance:', error);
      return null;
    }
  },

  // Get attendance by KVS grouping for comparison chart
  getAttendanceByKVSGrouping: async (page: number = 1, year: number, month: number): Promise<AttendanceByKVSGroupingResponse | null> => {
    try {
      const formattedMonth = String(month).padStart(2, '0');
      const data = await apiGet(`/pis/attendancebykvsgrouping?page=${page}&year=${year}&month=${formattedMonth}`);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching attendance by KVS grouping:', error);
      return null;
    }
  },

  // Get daily attendance by KVS (no paging) - optional year/month/date/kvid
  getDailyAttendanceByKVSNoPaging: async (year?: number, month?: number, date?: number, kvid?: number): Promise<any | null> => {
    try {
      let url = `/pis/dailyattendancebykvsnopaging`;
      const params: string[] = [];
      if (year !== undefined) params.push(`year=${year}`);
      if (month !== undefined) params.push(`month=${String(month).padStart(2, '0')}`);
      if (date !== undefined) params.push(`date=${String(date).padStart(2, '0')}`);
      if (kvid !== undefined) params.push(`kvid=${kvid}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching daily attendance by KVS (no paging):', error);
      return null;
    }
  },

  // Get historical KV attendance with date range (paginated)
  getHistoricalKVAttendance: async (page: number = 1, fromDate?: string, toDate?: string, orderBy?: string, order?: 'ASC' | 'DESC'): Promise<any | null> => {
    try {
      let url = `/pis/pdailyattendancebykvs?page=${page}`;
      if (fromDate) url += `&fromdate=${fromDate}`;
      if (toDate) url += `&todate=${toDate}`;
      if (orderBy) url += `&orderby=${orderBy}`;
      if (order) url += `&order=${order}`;
      
      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching historical KV attendance:', error);
      return null;
    }
  },

  // Get RO summary (for regional attendance heatmap)
  getROSummary: async (fromDate: string, toDate: string): Promise<any | null> => {
    try {
      let url = `/pis/rosummary?fromdate=${fromDate}&todate=${toDate}`;
      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching RO summary:', error);
      return null;
    }
  },

  // Get top performing summary (for charts and trends)
  getTopPerformingSummary: async (fromDate: string, toDate: string, flag: 'kv' | 'ro' = 'kv', limit: number = 15, order: 'ASC' | 'DESC' = 'DESC'): Promise<any | null> => {
    try {
      let url = `/pis/toperformingsummary?fromdate=${fromDate}&todate=${toDate}&flag=${flag}&limit=${limit}&order=${order}`;
      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching top performing summary:', error);
      return null;
    }
  },

  // Get attendance percentage for date range
  getAttendancePercDateRange: async (fromDate: string, toDate: string): Promise<any | null> => {
    try {
      let url = `/pis/attendancepercdaterange?fromdate=${fromDate}&todate=${toDate}`;
      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching attendance percentage date range:', error);
      return null;
    }
  },

  // Get employee register data by month and year
  getEmployeeRegister: async (empId: number | string, month?: number, year?: number): Promise<any | null> => {
    try {
      let url = `/pis/register?empid=${empId}`;
      if (month !== undefined) url += `&month=${month}`;
      if (year !== undefined) url += `&year=${year}`; // year should be 2-digit e.g. 25
      
      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching employee register:', error);
      return null;
    }
  },

  // Get KV-level register data (all employees in a KV for a given month/year)
  getKVRegisterData: async (kvId: number, month: number, year: number): Promise<any | null> => {
    try {
      const twoDigitYear = String(year).slice(-2);
      const url = `/pis/register?kvid=${kvId}&month=${month}&year=${twoDigitYear}`;
      const data = await apiGet(url);
      if (data && data.success) return data;
      return null;
    } catch (error) {
      console.error('Error fetching KV register data:', error);
      return null;
    }
  },


  // Get student details by student ID
  getStudentDetails: async (studentId: number | string): Promise<any | null> => {
    try {
      const data = await apiGet(`/sis/studentdetails?studentid=${studentId}`);
      if (data && data.success) {
        return Array.isArray(data.data) ? data.data[0] : data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching student details:', error);
      return null;
    }
  },

  // Get student guardian details
  getStudentGuardianDetails: async (studentId: number | string): Promise<any | null> => {
    try {
      const data = await apiGet(`/sis/guardiandetails?studentid=${studentId}`);
      if (data && data.success) {
        return Array.isArray(data.data) ? data.data[0] : data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching student guardian details:', error);
      return null;
    }
  },

  // Get student address profile
  getStudentAddressProfile: async (studentId: number | string): Promise<any | null> => {
    try {
      const data = await apiGet(`/sis/addressprofile?studentid=${studentId}`);
      if (data && data.success) {
        return Array.isArray(data.data) ? data.data[0] : data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching student address profile:', error);
      return null;
    }
  },

  // Get student first admission details
  getStudentFirstAdmissionDetails: async (studentId: number | string): Promise<any | null> => {
    try {
      const data = await apiGet(`/sis/firstadmissiondetails?studentid=${studentId}`);
      if (data && data.success) {
        return Array.isArray(data.data) ? data.data[0] : data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching student first admission details:', error);
      return null;
    }
  },

  // Get student present KV details
  getStudentPresentKVDetails: async (studentId: number | string): Promise<any | null> => {
    try {
      const data = await apiGet(`/sis/presentkvsdetails?studentid=${studentId}`);
      if (data && data.success) {
        return Array.isArray(data.data) ? data.data[0] : data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching student present KV details:', error);
      return null;
    }
  },

  // Get student current academic details
  getStudentCurrentAcademicDetails: async (studentId: number | string): Promise<any | null> => {
    try {
      const data = await apiGet(`/sis/currentacademicsessiondetails?studentid=${studentId}`);
      if (data && data.success) {
        return Array.isArray(data.data) ? data.data[0] : data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching student current academic details:', error);
      return null;
    }
  },

  // Get student previous session details
  getStudentPreviousSessionDetails: async (studentId: number | string): Promise<any | null> => {
    try {
      const data = await apiGet(`/sis/previoussessiondetails?studentid=${studentId}`);
      if (data && data.success) {
        return Array.isArray(data.data) ? data.data[0] : data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching student previous session details:', error);
      return null;
    }
  },

  // Get all regions for filter dropdowns
  getAllRegions: async (): Promise<any[]> => {
    try {
      const data = await apiGet('/shared/allros');
      if (data && data.success && Array.isArray(data.data)) {
        // Filter out "Abroad KV" region
        return data.data.filter((region: any) => region.roName !== 'Abroad KV');
      }
      return [];
    } catch (error) {
      console.error('Error fetching regions:', error);
      return [];
    }
  },

  // Get all states for filter dropdowns (includes last_updated)
  getAllStates: async (): Promise<any[]> => {
    try {
      const data = await apiGet('/pis/allstate');
      if (data && data.success && Array.isArray(data.data)) {
        return data.data.map((state: any) => ({
          stateId: state.state_id,
          stateName: state.state_name,
          isActive: state.is_active === '1',
          lastUpdated: state.last_updated
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching states:', error);
      return [];
    }
  },

  // Get all districts for filter dropdowns
  getAllDistricts: async (): Promise<any[]> => {
    try {
      const data = await apiGet('/pis/alldistrict');
      if (data && data.success && Array.isArray(data.data)) {
        return data.data.map((district: any) => ({
          districtId: district.district_id,
          districtName: district.district_name,
          stateId: district.state,
          isActive: district.is_active
        }));
      }
      return [];
    } catch (error) {
      console.error('Error fetching districts:', error);
      return [];
    }
  },

  // Get KV hierarchy data (regions -> states -> districts -> KVs)
  getKVHierarchyData: async (): Promise<any> => {
    try {
      const data = await apiGet('/kvis/kvprofile/rostoalldistrictstoallkvs');
      if (data && data.success) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching KV hierarchy data:', error);
      return null;
    }
  },

  // Get top/bottom performing employees by date range
  // Used for: Most Improved (DESC), High Risk/Declining (ASC)
  getTopPerformingEmployees: async (
    fromDate: string, 
    toDate: string, 
    flag: 'regular' | 'contractual' = 'regular', 
    order: 'ASC' | 'DESC' = 'DESC',
    limit: number = 20
  ): Promise<any | null> => {
    try {
      const url = `/pis/topperformingempdaterange?fromdate=${fromDate}&todate=${toDate}&flag=${flag}&order=${order}&limit=${limit}`;
      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching top performing employees:', error);
      return null;
    }
  },

  // Get KV attendance summary (using dailyattendancebykvsnopaging)
  getKVAttendanceSummary: async (page: number = 1, filters: { roId?: number; stateId?: number; year?: number; month?: number; date?: number }): Promise<any> => {
    try {
      // Use dailyattendancebykvsnopaging as the source
      let url = `/pis/dailyattendancebykvsnopaging`;
      const params: string[] = [];
      if (filters.year !== undefined) params.push(`year=${filters.year}`);
      if (filters.month !== undefined) params.push(`month=${String(filters.month).padStart(2, '0')}`);
      if (filters.date !== undefined) params.push(`date=${String(filters.date).padStart(2, '0')}`);
      if (params.length > 0) url += `?${params.join('&')}`;

      const data = await apiGet(url);
      if (data && data.success && Array.isArray(data.data)) {
        // Filter by region/state if specified
        let filteredData = data.data;
        if (filters.roId) {
          filteredData = filteredData.filter((kv: any) => kv.roId === filters.roId);
        }
        if (filters.stateId) {
          filteredData = filteredData.filter((kv: any) => kv.stateId === filters.stateId);
        }
        
        // Paginate client-side (50 per page)
        const pageSize = 50;
        const startIndex = (page - 1) * pageSize;
        const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);
        
        return {
          data: paginatedData,
          pagination: {
            currentPage: page,
            totalPages: Math.ceil(filteredData.length / pageSize),
            totalItems: filteredData.length,
            pageSize: pageSize
          }
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching KV attendance summary:', error);
      return null;
    }
  },

  // Get employee attendance by KV and date range
  getEmployeeAttendanceByKV: async (
    page: number = 1,
    kvId?: number,
    fromDate?: string,
    toDate?: string,
    empType?: 'regular' | 'contractual'
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      if (kvId) params.append('kvid', String(kvId));
      if (fromDate && toDate && fromDate !== toDate) {
        // Date range: pass fromdate & todate
        params.append('fromdate', fromDate);
        params.append('todate', toDate);
      } else if (fromDate) {
        // Single day: pass year/month/date separately (2-digit year)
        const d = new Date(fromDate);
        params.append('year', String(d.getFullYear()).slice(-2));
        params.append('month', String(d.getMonth() + 1).padStart(2, '0'));
        params.append('date', String(d.getDate()).padStart(2, '0'));
      }
      // Always pass emptype (defaults to 'regular')
      params.append('emptype', empType || 'regular');
      console.log('Fetching employee attendance with params:', params.toString());
      const data = await apiGet(`/pis/employeeattendance?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching employee attendance:', error);
      return null;
    }
  },

  // Get student attendance by KV with filters (for analytics)
  getStudentAttendanceByKV: async (
    page: number = 1,
    fromDate: string,
    toDate: string,
    roId?: number,
    orderBy: 'presentPercentage' | 'boysPresentPercentage' | 'girlsPresentPercentage' = 'presentPercentage',
    order: 'ASC' | 'DESC' = 'DESC',
    gender?: number
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('fromdate', fromDate);
      params.append('todate', toDate);
      params.append('orderby', orderBy);
      params.append('order', order);
      if (roId) params.append('roid', String(roId));
      if (gender !== undefined) params.append('gender', String(gender));
      
      const data = await apiGet(`/sis/pstudentattendance?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching student attendance by KV:', error);
      return null;
    }
  },

  // Legacy alias for existing student attendance API method
  getStudentAttendance: async (
    page: number = 1,
    fromDate: string,
    toDate: string,
    roId?: number,
    orderBy: 'presentPercentage' | 'boysPresentPercentage' | 'girlsPresentPercentage' = 'presentPercentage',
    order: 'ASC' | 'DESC' = 'DESC',
    gender?: number
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('fromdate', fromDate);
      params.append('todate', toDate);
      params.append('orderby', orderBy);
      params.append('order', order);
      if (roId) params.append('roid', String(roId));
      if (gender !== undefined) params.append('gender', String(gender));

      const data = await apiGet(`/sis/pstudentattendance?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching student attendance by KV (legacy alias):', error);
      return null;
    }
  },

  // Get student attendance by Region with filters (for analytics)
  getStudentAttendanceByRegion: async (
    page: number = 1,
    fromDate: string,
    toDate: string,
    orderBy: 'attendancePercentage' | '1' | '2' = 'attendancePercentage',
    order: 'ASC' | 'DESC' = 'DESC'
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('fromdate', fromDate);
      params.append('todate', toDate);
      params.append('orderby', orderBy);
      params.append('order', order);
      
      const data = await apiGet(`/sis/misrosummary?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching student attendance by region:', error);
      return null;
    }
  },

  // Get class-wise student attendance data
  getClassWiseData: async (
    page: number = 1,
    fromDate: string,
    toDate: string
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('fromdate', fromDate);
      params.append('todate', toDate);
      
      const data = await apiGet(`/sis/classwisedata?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching class-wise data:', error);
      return null;
    }
  },

  // Get class-wise section data for a specific KV
  getClassWiseSectionData: async (
    kvId: string,
    page: number = 1,
    fromDate: string,
    toDate: string
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('fromdate', fromDate);
      params.append('todate', toDate);
      params.append('kvid', kvId);
      
      const data = await apiGet(`/sis/classwisesectiondata?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching class-wise section data:', error);
      return null;
    }
  },

  // Get all over section-wise attendance for a specific class
  getSchoolWiseSectionAttendance: async (
    classNum: number,
    page: number = 1,
    fromDate: string,
    toDate: string
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('fromdate', fromDate);
      params.append('todate', toDate);
      params.append('orderby', 'class');
      params.append('order', 'desc');
      params.append('class', String(classNum));
      
      const data = await apiGet(`/sis/alloversectionwiseattendance?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching school-wise section attendance:', error);
      return null;
    }
  },

  // Get total class-wise section data (aggregated totals for a class)
  getTotalClassWiseSectionData: async (
    classNum?: number,
    fromDate?: string,
    toDate?: string
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      if (fromDate) params.append('fromdate', fromDate);
      if (toDate) params.append('todate', toDate);
      if (classNum !== undefined && classNum !== null && classNum !== -999) {
        params.append('class', String(classNum));
      }
      
      const data = await apiGet(`/sis/totalclasswisesectiondata?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching total class-wise section data:', error);
      return null;
    }
  },

  // Get students in a specific class and section
  getClassSectionStudents: async (
    kvId: number | string,
    classNum: number | string,
    section: string,
    page: number = 1,
    fromDate?: string,
    toDate?: string,
    orderBy: string = 'firstName',
    order: 'ASC' | 'DESC' = 'ASC'
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('kvid', String(kvId));
      params.append('class', String(classNum));
      params.append('section', section);
      params.append('page', String(page));
      params.append('orderby', orderBy);
      params.append('order', order);
      if (fromDate) params.append('fromdate', fromDate);
      if (toDate) params.append('todate', toDate);
      
      const data = await apiGet(`/sis/kvsclasssectionwisestudents?${params.toString()}`);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching class section students:', error);
      return null;
    }
  },

  // Get RO-wise KV school data for student attendance
  getROWiseKVData: async (
    roId: number,
    page: number = 1,
    fromDate: string,
    toDate: string
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('fromdate', fromDate);
      params.append('todate', toDate);
      params.append('roid', String(roId));
      
      const data = await apiGet(`/sis/rowisedata?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching RO-wise KV data:', error);
      return null;
    }
  },

  // Get RO (Regional Office) Summary for Staff Attendance Analytics
  getROAttendanceSummary: async (
    fromDate: string,
    toDate: string
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('fromdate', fromDate);
      params.append('todate', toDate);
      
      const data = await apiGet(`/pis/rosummary?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching RO attendance summary:', error);
      return null;
    }
  },

  // Get paginated daily attendance by KVs with RO filter
  getPaginatedDailyAttendanceByKVS: async (
    page: number = 1,
    fromDate: string,
    toDate: string,
    roId?: number,
    orderBy?: string,
    order?: 'ASC' | 'DESC'
  ): Promise<any> => {
    try {
      const params = new URLSearchParams();
      params.append('page', String(page));
      params.append('fromdate', fromDate);
      params.append('todate', toDate);
      if (roId) params.append('roid', String(roId));
      if (orderBy) params.append('orderby', orderBy);
      if (order) params.append('order', order);
      
      const data = await apiGet(`/pis/pdailyattendancebykvs?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('Error fetching paginated daily attendance by KVS:', error);
      return null;
    }
  },

  // Get today's detailed attendance with region-wise breakdown and summary
  getTodaysDetailedAttendance: async (date?: string): Promise<any> => {
    try {
      const url = date
        ? `/sis/todays-detailed-attendance?date=${date}`
        : '/sis/todays-detailed-attendance';
      const data = await apiGet(url);
      if (data && data.success) {
        return data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching today\'s detailed attendance:', error);
      return null;
    }
  },

};